/**
 * Textes de l'orchestration de la reprise (ARTO-132) : le plan, la progression
 * d'un lot, l'écartement des lignes refusées, le delta, l'annulation, le
 * scellement et les références d'origine. Fichier à part ; ses blocs sont
 * rangés sous la clé `plan` du namespace `console` (D15), d'où `plan.plan.*`
 * pour le plan lui-même. Voix 2.2 : phrases courtes, un geste porte le même
 * nom du bouton au message de fin.
 */

export const frPlan = {
  plan: {
    titre: 'Plan de reprise',
    texte:
      'Le plan lance d’un coup tous les lots prêts, dans l’ordre où les données dépendent les unes des autres. Une étape ne démarre que lorsque la précédente est terminée.',
    chargement: 'Lecture du plan…',
    erreur: 'Impossible de lire le plan.',
    aucun: 'Aucun plan pour cette galerie. Quand des lots sont prêts, lancez-les d’un coup.',
    aucunPret: 'Aucun lot n’est prêt à lancer : essayez d’abord les lots déposés.',
    etats: {
      en_cours: 'En cours',
      en_pause: 'En pause',
      bloque: 'Bloqué',
      termine: 'Terminé',
      abandonne: 'Abandonné',
    },
    resume: 'Étape {{etape}} sur {{total}}',
    meta: 'Lancé par {{operateur}} le {{date}}',
    termineLe: 'Terminé le {{date}}',
    rafraichi: 'La page se met à jour toutes les 3 secondes.',
    etapes: {
      titre: 'Les étapes du plan',
      numero: 'Étape {{etape}}',
      lots_one: '{{count}} lot',
      lots_other: '{{count}} lots',
      sansLot: 'Aucun lot',
      progression: '{{termines}} terminé sur {{lots}}',
      echoues_one: '{{count}} en échec',
      echoues_other: '{{count}} en échec',
      enCours_one: '{{count}} en cours',
      enCours_other: '{{count}} en cours',
      courante: 'En cours',
      faite: 'Terminée',
      attente: 'À venir',
      lotsDeLEtape: 'Lots de l’étape {{etape}}',
    },
    gestes: {
      lancer: 'Lancer le plan',
      lancerAide_one: 'Lance le lot prêt, étape par étape.',
      lancerAide_other: 'Lance les {{count}} lots prêts, étape par étape.',
      pause: 'Pause',
      pauseAide:
        'La pause n’arrête pas ce qui tourne : les lots en cours finissent, aucun autre ne part.',
      reprendre: 'Reprendre',
      reprendreAide: 'Le plan repart de l’étape où il s’est arrêté.',
      abandonner: 'Abandonner',
      mfa: 'Ce plan contient des factures antérieures ou des documents confidentiels : la vérification du second facteur vous sera demandée.',
      lance: 'Plan lancé.',
      enPause: 'Plan en pause.',
      repris: 'Plan repris.',
      abandonne: 'Plan abandonné.',
    },
    abandon: {
      titre: 'Abandonner ce plan ?',
      texte:
        'Le plan est clos et ne reprend plus. Les lots en cours finissent, les lots prêts restent prêts : un nouveau plan pourra les lancer.',
      confirmer: 'Abandonner le plan',
      garder: 'Garder le plan',
    },
    arret: {
      titre: 'Le plan est arrêté',
      lotsEnCause: 'Lots en cause',
      ouvrir: 'Ouvrir le lot de {{nature}}',
      raisons: {
        lots_en_echec:
          'Un lot a échoué. Ouvrez-le, lisez la cause, relancez-le, puis reprenez le plan.',
        lots_non_prets:
          'Un lot n’est plus prêt : il a été remis à l’essai. Essayez-le de nouveau, puis reprenez le plan.',
        lot_introuvable:
          'Un lot du plan n’existe plus. Abandonnez ce plan et lancez-en un nouveau.',
        mandat_absent:
          'Le mandat de reprise de la galerie n’est plus en vigueur. Posez-en un autre, puis reprenez le plan.',
        nature_hors_mandat:
          'Le mandat en vigueur ne couvre plus la nature d’un lot. Demandez un mandat plus large, puis reprenez le plan.',
        depot_purge:
          'Le dépôt d’un lot est purgé, ou en cours de purge : ses fichiers ne se reprennent plus. Déposez de nouveau les fichiers dans un nouveau lot.',
        travail_echoue:
          'Le travail qui fait avancer le plan s’est arrêté. Reprendre le plan le relance.',
        inconnue: 'Le plan s’est arrêté (code {{raison}}). Ouvrez les lots en cause.',
      },
    },
    sceller: {
      titre: 'Scellement',
      texte:
        'Quand la reprise est vérifiée, scellez-la : les lots terminés ou échoués sont figés et ne s’annulent plus. Réservé à la direction de Venio.',
      bouton: 'Sceller la reprise',
      rien: 'Aucun lot terminé ou échoué à sceller.',
      planActif: 'Terminez le plan ou abandonnez-le avant de sceller.',
      scelleLe_one: '{{count}} lot est scellé : il ne s’annule plus.',
      scelleLe_other: '{{count}} lots sont scellés : ils ne s’annulent plus.',
      confirmationTitre: 'Sceller la reprise ?',
      confirmationTexte:
        'Les lots terminés ou échoués de cette galerie passent à « Scellé ». Ensuite, plus aucun lot ne peut être annulé. Ce geste est définitif.',
      confirmationMfa: 'Il exige un second facteur récent : la vérification vous sera demandée.',
      confirmer: 'Sceller la reprise',
      garder: 'Ne pas sceller',
      ok_one: '{{count}} lot scellé.',
      ok_other: '{{count}} lots scellés.',
    },
  },

  progression: {
    titre: 'Progression',
    faits: '{{faits}} sur {{total}}',
    faitsSansTotal: '{{faits}} faits',
    libelle: 'Avancement du travail',
    demarrage: 'Le travail démarre…',
    etapes: {
      essai: 'Essai',
      import: 'Import',
      annulation: 'Annulation',
    },
    etape: 'Étape : {{etape}}',
    restant: 'Temps restant estimé : {{duree}}',
    restantInconnu: 'Temps restant : estimation en cours',
    moinsDUneMinute: 'moins d’une minute',
    priorite: 'Priorité {{niveau}}',
    priorites: { basse: 'basse', normale: 'normale', haute: 'haute' },
    prioriteAide: 'La reprise cède la place au travail courant de la galerie.',
    rafraichi: 'La page se met à jour toutes les 3 secondes.',
    raison: 'Dernier refus : {{texte}}',
    raisons: {
      ecartement_perime:
        'L’écartement ne décrit plus les fichiers de ce lot. Refaites l’essai, puis écartez de nouveau les lignes refusées.',
      lot_scelle: 'Ce lot est scellé : il ne s’annule plus.',
      reprise_close: 'La bascule a commencé : la reprise de cette galerie est close.',
      lot_mis_de_cote: 'Ce lot a été mis de côté au passage en recette : il ne se lance plus.',
      galerie_en_service:
        'La galerie est en service : ses lots ne s’exécutent plus ni ne s’annulent.',
      depot_reinventorie:
        'Le dépôt a été réinventorié depuis le dernier essai. Refaites l’essai de ce lot.',
      mandat_absent: 'Le mandat de reprise n’est plus en vigueur.',
      nature_hors_mandat: 'Le mandat en vigueur ne couvre pas ce lot.',
      depot_purge: 'Le dépôt de ce lot est purgé : ses fichiers ne se reprennent plus.',
    },
    brute: 'code {{code}}',
  },

  ecartement: {
    titre: 'Lignes refusées à l’essai',
    explication:
      'Vous pouvez écarter ces lignes : elles ne seront pas reprises, et le fichier des rejets les garde pour correction. Le reste du lot devient prêt à lancer. Sinon, corrigez les fichiers et déposez un nouveau lot.',
    explicationMorceaux:
      'Chaque morceau essayé est écarté en même temps, avec le même motif. Les lignes refusées ne seront pas reprises.',
    bouton_one: 'Écarter {{count}} ligne et autoriser l’import',
    bouton_other: 'Écarter {{count}} lignes et autoriser l’import',
    dialogue: {
      titre: 'Écarter les lignes refusées ?',
      texte_one:
        '{{count}} ligne refusée ne sera pas reprise. Le lot devient prêt à lancer ; les autres lignes se reprennent normalement.',
      texte_other:
        '{{count}} lignes refusées ne seront pas reprises. Le lot devient prêt à lancer ; les autres lignes se reprennent normalement.',
      motif: 'Motif de l’écartement',
      motifAide:
        'Obligatoire, de 3 à 500 caractères. Le motif, votre nom et la date restent attachés au lot.',
      motifRequis: 'Écrivez le motif de l’écartement, trois caractères au moins.',
      motifTropLong: 'Le motif dépasse 500 caractères.',
      compteur: '{{nombre}} sur {{max}}',
      confirmer: 'Écarter et autoriser l’import',
      garder: 'Garder le lot tel quel',
    },
    ok_one: '{{count}} ligne écartée : le lot est prêt.',
    ok_other: '{{count}} lignes écartées : le lot est prêt.',
    telecharger: 'Télécharger les rejets (CSV)',
    telechargerNature: 'Télécharger les rejets : {{nature}} (CSV)',
    telechargerFeuille: 'Télécharger les rejets : feuille {{feuille}} (CSV)',
    lignes_one: '{{count}} ligne',
    lignes_other: '{{count}} lignes',
    fait: {
      titre: 'Lignes écartées',
      resume_one: '{{count}} ligne écartée par {{par}}, le {{date}}.',
      resume_other: '{{count}} lignes écartées par {{par}}, le {{date}}.',
      motif: 'Motif : {{motif}}',
      aide: 'Ces lignes ne sont pas reprises. Corrigez-les dans le fichier des rejets, puis déposez-les dans un nouveau lot.',
      compteur: 'Écartées',
    },
    perime:
      'L’écartement ne décrit plus les fichiers de ce lot : refaites l’essai, puis écartez de nouveau les lignes refusées.',
  },

  delta: {
    titre: 'À revoir : lignes signalées',
    texte:
      'Ces lignes sont connues par leur référence d’origine, et leur fiche a changé dans l’ancien logiciel depuis sa reprise. Les fiches de la galerie sont intactes. Ces lignes ne sont pas des refus ; revoyez-les à la main.',
    rienEcrase: 'Rien n’a été écrasé.',
    ligne: 'Ligne {{ligne}} · {{libelle}}',
    fiche: 'Fiche {{id}}',
    signalees: 'Lignes signalées',
  },

  origine: {
    libelle: 'Référence d’origine : source {{source}}, identifiant {{id}}',
    libelleSansSource: 'Référence d’origine : identifiant {{id}}, sans source',
    collision: {
      titre: 'Collision entre sources',
      texte:
        'Cette fiche existe déjà, reprise depuis la source ci-dessus. La ligne n’y est pas rattachée et rien n’y est complété. Vérifiez l’application d’origine du profil de ce lot : deux logiciels peuvent numéroter pareil.',
    },
  },

  annulation: {
    titre: 'Annulation',
    texte:
      'Défait ce que ce lot a créé chez la galerie, sauf ce qui a bougé depuis. Réservé à la direction de Venio.',
    bouton: 'Annuler ce lot',
    rejouer: 'Rejouer l’annulation',
    aideAnnuler: 'Un second facteur récent est exigé.',
    dialogue: {
      titre: 'Annuler ce lot ?',
      intro:
        'Venio va défaire la reprise de ce lot chez la galerie, par paquets d’une cinquantaine de fiches.',
      supprimeTitre: 'Sera supprimé',
      supprime1: 'Les fiches que ce lot a créées, si elles sont telles qu’il les a laissées.',
      supprime2:
        'Ce qui est venu avec elles : images, artiste créé en passant, et leurs fichiers (originaux, variantes, pièces).',
      conserveTitre: 'Sera gardé',
      conserve1: 'Toute fiche modifiée depuis la reprise.',
      conserve2:
        'Toute fiche que quelque chose désigne : une réservation, une facture, une exposition, un autre lot… Le rapport dit laquelle.',
      conserve3:
        'Les champs que ce lot a seulement complétés : une complétion ne se défait jamais.',
      apresTitre: 'Ensuite',
      apres1: 'Le lot ne se relance plus. Pour reprendre ses lignes, déposez un nouveau lot.',
      apres2: 'Aucun lot de la galerie ne démarre tant que l’annulation n’est pas faite.',
      mfa: 'Elle exige un second facteur récent : la vérification vous sera demandée.',
      prevenue: 'J’ai prévenu la galerie.',
      prevenueAide:
        'Une galerie invitée tôt peut déjà écrire : une écriture pendant l’annulation pourrait laisser une référence pendante.',
      confirmer: 'Annuler ce lot',
      garder: 'Ne pas annuler',
    },
    suivi: {
      titre: 'Annulation',
      demandee: 'Annulation demandée par {{par}} le {{date}}. Elle va démarrer.',
      en_cours: 'Annulation en cours, demandée par {{par}} le {{date}}.',
      echouee: 'L’annulation a échoué. Rejouez-la : elle reprend et ne défait que ce qui reste.',
      faite: 'Annulation faite le {{date}}, demandée par {{par}}.',
    },
    rapport: {
      titre: 'Rapport d’annulation',
      compteurs: 'Compteurs de l’annulation',
      supprimes: 'Fiches supprimées',
      conserves: 'Fiches gardées',
      dejaAbsents: 'Déjà absentes',
      nonDefaites: 'Compléments gardés',
      nonDefaitesAide: 'Des champs vides remplis par ce lot : une complétion ne se défait jamais.',
      medias: 'Médias retirés',
      mediasAide: 'Originaux, variantes et pièces retirés du stockage.',
      sansCible_one: '{{count}} création sans fiche connue (reçu antérieur) : elle reste.',
      sansCible_other: '{{count}} créations sans fiche connue (reçus antérieurs) : elles restent.',
      supprimesParType: 'Supprimées, par type',
      gardees: 'Fiches gardées, et pourquoi',
      aucuneGardee: 'Rien n’est gardé : tout ce que le lot avait créé est parti.',
      dejaAbsentes: 'Déjà absentes',
      fiche: '{{type}} {{id}}',
      ligne: 'ligne {{numero}}',
      et_one: 'et {{count}} autre',
      et_other: 'et {{count}} autres',
      indisponible: 'Ce rapport est indisponible.',
      chargement: 'Lecture du rapport…',
      modeles: {
        Location: 'Lieu',
        Artist: 'Artiste',
        Artwork: 'Œuvre',
        Contact: 'Contact',
        Exhibition: 'Exposition',
        FactureHistorique: 'Facture antérieure',
        Document: 'Document',
        Media: 'Image',
      },
      motifs: {
        modifie: 'Modifiée depuis la reprise',
        etat_inconnu: 'Reprise avant le suivi des fiches : état inconnu, gardée par précaution',
        referencee: 'Désignée par {{type}}',
        autre_lot: 'Complétée par un autre lot',
        variantes: 'Ses variantes sont encore en cours de fabrication',
      },
      references: {
        oeuvre: 'une œuvre',
        exemplaire: 'un exemplaire',
        exposition: 'une exposition',
        accrochage: 'un accrochage',
        mouvement: 'un mouvement',
        actualite: 'une actualité',
        presse: 'une revue de presse',
        contrat: 'un contrat',
        contrat_artiste: 'un contrat d’artiste',
        document: 'un document',
        image: 'une image',
        image_principale: 'l’image principale d’une œuvre',
        portrait: 'un portrait d’artiste',
        logotype: 'un logotype',
        constat: 'la photo d’un constat',
        demande: 'une demande',
        reservation: 'une réservation',
        salle_privee: 'une salle privée',
        facture: 'une facture',
        facture_anterieure: 'une facture antérieure',
        certificat: 'un certificat',
        offre: 'une offre',
        rendez_vous: 'un rendez-vous',
        provenance: 'une provenance',
        cout_production: 'un coût de production',
        dossier: 'un dossier d’œuvres',
        newsletter: 'une campagne de newsletter',
        diffusion: 'une diffusion',
        commande: 'une commande de la boutique',
        ordre_artiste: 'l’ordre des œuvres d’un artiste',
        lieu: 'un lieu',
        vente: 'une vente',
        fusion: 'une fusion de contacts',
        consultation: 'une consultation de document',
        operation_stand: 'une opération du stand',
      },
      motifInconnu: 'Gardée ({{motif}})',
    },
    morceaux: {
      titre: 'Ce lot se découpe en morceaux',
      texte:
        'On ne l’annule pas d’un bloc : annulez ses morceaux un par un, dans l’ordre inverse du plan, du dernier au premier.',
      ordre: 'Ordre à suivre',
      ouvrir: 'Ouvrir le morceau {{rang}} de {{nature}}',
      nom: '{{nature}}, morceau {{rang}}',
    },
    lotAnnule: {
      titre: 'Lot annulé',
      texte:
        'Ce lot est annulé : il ne se relance plus. Pour reprendre ses lignes, déposez un nouveau lot.',
    },
    scelle: {
      titre: 'Lot scellé',
      texte: 'Scellé le {{date}} : ce lot ne s’annule plus.',
    },
    badges: {
      scelle: 'Scellé',
      annule: 'Annulé',
      annulationEnCours: 'Annulation en cours',
    },
    planActif:
      'Un plan occupe la galerie, même en pause ou bloqué : abandonnez-le avant d’annuler un lot.',
    importEnCours: 'Un import tourne dans la galerie : attendez qu’il se termine avant d’annuler.',
    galerieEnService:
      'La galerie est en service : ses lots ne s’annulent plus. Une écriture de la galerie pourrait désigner ce que l’annulation supprimerait.',
  },

  erreurs: {
    rien_a_lancer:
      'Aucun lot n’est prêt à lancer dans cette galerie. Essayez d’abord les lots déposés.',
    plan_en_cours: 'Un plan de reprise occupe déjà cette galerie. Reprenez-le ou abandonnez-le.',
    plan_absent: 'Ce geste ne correspond à aucun plan : il a peut-être changé. Rechargez la page.',
    plan_modifie: 'Le plan vient de changer. Rechargez la page.',
    plan_actif:
      'Un plan occupe la galerie, même en pause ou bloqué. Abandonnez-le avant d’annuler un lot ou de sceller la reprise.',
    annulation_en_cours:
      'Une annulation n’est pas faite dans cette galerie. Aucun lot ne se lance et la reprise ne se scelle pas tant qu’elle n’est pas terminée. Rejouez-la si elle a échoué.',
    reprise_en_cours: 'Un import de reprise tourne dans cette galerie. Attendez qu’il se termine.',
    rien_a_ecarter: 'L’essai n’a refusé aucune ligne : il n’y a rien à écarter.',
    lot_non_essaye: 'Ce lot doit avoir été essayé avant d’en écarter des lignes.',
    essai_a_refaire:
      'Cet essai ne dit pas quelles lignes il a refusées. Refaites l’essai avant d’écarter.',
    ecartement_perime:
      'L’écartement ne décrit plus les fichiers de ce lot. Refaites l’essai, puis écartez de nouveau.',
    lot_scelle: 'Ce lot est scellé : il ne s’annule plus.',
    reprise_close: 'La bascule a commencé : la reprise de cette galerie est close.',
    lot_mis_de_cote: 'Ce lot a été mis de côté au passage en recette : il ne se lance plus.',
    lot_annule:
      'Ce lot a été annulé : il ne se relance plus. Déposez un nouveau lot pour reprendre ses lignes.',
    lot_non_execute: 'Seul un lot terminé ou échoué peut être annulé.',
    galerie_en_service:
      'La galerie est en service, ou n’a pas de dossier d’ouverture : ses lots ne s’annulent plus.',
    lot_meme_nature_en_cours:
      'Un lot de cette nature tourne déjà dans la galerie. Attendez qu’il se termine.',
  },
};

export const enPlan: typeof frPlan = {
  plan: {
    titre: 'Migration plan',
    texte:
      'The plan launches every ready batch at once, in the order the data depends on itself. A step only starts once the previous one is finished.',
    chargement: 'Reading the plan…',
    erreur: 'Could not read the plan.',
    aucun: 'No plan for this gallery. When batches are ready, launch them all at once.',
    aucunPret: 'No batch is ready to launch: try the uploaded batches first.',
    etats: {
      en_cours: 'Running',
      en_pause: 'Paused',
      bloque: 'Blocked',
      termine: 'Finished',
      abandonne: 'Abandoned',
    },
    resume: 'Step {{etape}} of {{total}}',
    meta: 'Launched by {{operateur}} on {{date}}',
    termineLe: 'Finished on {{date}}',
    rafraichi: 'The page refreshes every 3 seconds.',
    etapes: {
      titre: 'The plan steps',
      numero: 'Step {{etape}}',
      lots_one: '{{count}} batch',
      lots_other: '{{count}} batches',
      sansLot: 'No batch',
      progression: '{{termines}} of {{lots}} finished',
      echoues_one: '{{count}} failed',
      echoues_other: '{{count}} failed',
      enCours_one: '{{count}} running',
      enCours_other: '{{count}} running',
      courante: 'Running',
      faite: 'Finished',
      attente: 'Upcoming',
      lotsDeLEtape: 'Batches of step {{etape}}',
    },
    gestes: {
      lancer: 'Launch the plan',
      lancerAide_one: 'Launches the ready batch, step by step.',
      lancerAide_other: 'Launches the {{count}} ready batches, step by step.',
      pause: 'Pause',
      pauseAide:
        'Pausing does not stop what is running: running batches finish, no other one starts.',
      reprendre: 'Resume',
      reprendreAide: 'The plan restarts from the step where it stopped.',
      abandonner: 'Abandon',
      mfa: 'This plan holds earlier invoices or confidential documents: you will be asked for the second factor.',
      lance: 'Plan launched.',
      enPause: 'Plan paused.',
      repris: 'Plan resumed.',
      abandonne: 'Plan abandoned.',
    },
    abandon: {
      titre: 'Abandon this plan?',
      texte:
        'The plan is closed and does not restart. Running batches finish, ready batches stay ready: a new plan can launch them.',
      confirmer: 'Abandon the plan',
      garder: 'Keep the plan',
    },
    arret: {
      titre: 'The plan has stopped',
      lotsEnCause: 'Batches at fault',
      ouvrir: 'Open the {{nature}} batch',
      raisons: {
        lots_en_echec:
          'A batch failed. Open it, read the cause, relaunch it, then resume the plan.',
        lots_non_prets:
          'A batch is no longer ready: it went back to trial. Try it again, then resume the plan.',
        lot_introuvable:
          'A batch of the plan no longer exists. Abandon this plan and launch a new one.',
        mandat_absent:
          'The gallery’s migration mandate is no longer in force. Set a new one, then resume the plan.',
        nature_hors_mandat:
          'The mandate in force no longer covers the kind of a batch. Ask for a wider mandate, then resume the plan.',
        depot_purge:
          'The deposit of a batch is purged, or being purged: its files can no longer be migrated. Upload the files again in a new batch.',
        travail_echoue:
          'The job that moves the plan along has stopped. Resuming the plan restarts it.',
        inconnue: 'The plan has stopped (code {{raison}}). Open the batches at fault.',
      },
    },
    sceller: {
      titre: 'Sealing',
      texte:
        'Once the migration is checked, seal it: finished or failed batches are frozen and can no longer be cancelled. Reserved to Venio’s management.',
      bouton: 'Seal the migration',
      rien: 'No finished or failed batch to seal.',
      planActif: 'Finish the plan or abandon it before sealing.',
      scelleLe_one: '{{count}} batch is sealed: it can no longer be cancelled.',
      scelleLe_other: '{{count}} batches are sealed: they can no longer be cancelled.',
      confirmationTitre: 'Seal the migration?',
      confirmationTexte:
        'The finished or failed batches of this gallery become “Sealed”. After that, no batch can be cancelled any more. This cannot be undone.',
      confirmationMfa: 'It requires a recent second factor: you will be asked to verify.',
      confirmer: 'Seal the migration',
      garder: 'Do not seal',
      ok_one: '{{count}} batch sealed.',
      ok_other: '{{count}} batches sealed.',
    },
  },

  progression: {
    titre: 'Progress',
    faits: '{{faits}} of {{total}}',
    faitsSansTotal: '{{faits}} done',
    libelle: 'Job progress',
    demarrage: 'The job is starting…',
    etapes: {
      essai: 'Trial',
      import: 'Import',
      annulation: 'Cancellation',
    },
    etape: 'Step: {{etape}}',
    restant: 'Estimated time left: {{duree}}',
    restantInconnu: 'Time left: estimating',
    moinsDUneMinute: 'less than a minute',
    priorite: '{{niveau}} priority',
    priorites: { basse: 'Low', normale: 'Normal', haute: 'High' },
    prioriteAide: 'Migration gives way to the gallery’s current work.',
    rafraichi: 'The page refreshes every 3 seconds.',
    raison: 'Last refusal: {{texte}}',
    raisons: {
      ecartement_perime:
        'The set-aside rows no longer describe this batch’s files. Run the trial again, then set aside the refused rows again.',
      lot_scelle: 'This batch is sealed: it can no longer be cancelled.',
      reprise_close: 'The switchover has started: this gallery’s migration is closed.',
      lot_mis_de_cote: 'This batch was set aside when acceptance began: it no longer runs.',
      galerie_en_service: 'The gallery is live: its batches no longer run or get cancelled.',
      depot_reinventorie:
        'The deposit was inventoried again since the last trial. Run this batch’s trial again.',
      mandat_absent: 'The migration mandate is no longer in force.',
      nature_hors_mandat: 'The mandate in force does not cover this batch.',
      depot_purge: 'The deposit of this batch is purged: its files can no longer be migrated.',
    },
    brute: 'code {{code}}',
  },

  ecartement: {
    titre: 'Rows refused by the trial',
    explication:
      'You can set these rows aside: they will not be migrated, and the rejects file keeps them for correction. The rest of the batch becomes ready to launch. Otherwise, fix the files and upload a new batch.',
    explicationMorceaux:
      'Every tried chunk is set aside at the same time, with the same reason. The refused rows will not be migrated.',
    bouton_one: 'Set aside {{count}} row and allow the import',
    bouton_other: 'Set aside {{count}} rows and allow the import',
    dialogue: {
      titre: 'Set aside the refused rows?',
      texte_one:
        '{{count}} refused row will not be migrated. The batch becomes ready to launch; the other rows migrate normally.',
      texte_other:
        '{{count}} refused rows will not be migrated. The batch becomes ready to launch; the other rows migrate normally.',
      motif: 'Reason for setting aside',
      motifAide:
        'Required, 3 to 500 characters. The reason, your name and the date stay attached to the batch.',
      motifRequis: 'Write the reason for setting aside, at least three characters.',
      motifTropLong: 'The reason is longer than 500 characters.',
      compteur: '{{nombre}} of {{max}}',
      confirmer: 'Set aside and allow the import',
      garder: 'Keep the batch as it is',
    },
    ok_one: '{{count}} row set aside: the batch is ready.',
    ok_other: '{{count}} rows set aside: the batch is ready.',
    telecharger: 'Download the rejects (CSV)',
    telechargerNature: 'Download the rejects: {{nature}} (CSV)',
    telechargerFeuille: 'Download the rejects: sheet {{feuille}} (CSV)',
    lignes_one: '{{count}} row',
    lignes_other: '{{count}} rows',
    fait: {
      titre: 'Rows set aside',
      resume_one: '{{count}} row set aside by {{par}}, on {{date}}.',
      resume_other: '{{count}} rows set aside by {{par}}, on {{date}}.',
      motif: 'Reason: {{motif}}',
      aide: 'These rows are not migrated. Fix them in the rejects file, then upload them in a new batch.',
      compteur: 'Set aside',
    },
    perime:
      'The set-aside rows no longer describe this batch’s files: run the trial again, then set aside the refused rows again.',
  },

  delta: {
    titre: 'To review: flagged rows',
    texte:
      'These rows are known by their original reference, and their record has changed in the old software since it was migrated. The gallery’s records are untouched. These rows are not refusals; review them by hand.',
    rienEcrase: 'Nothing was overwritten.',
    ligne: 'Row {{ligne}} · {{libelle}}',
    fiche: 'Record {{id}}',
    signalees: 'Flagged rows',
  },

  origine: {
    libelle: 'Original reference: source {{source}}, identifier {{id}}',
    libelleSansSource: 'Original reference: identifier {{id}}, no source',
    collision: {
      titre: 'Clash between sources',
      texte:
        'This record already exists, migrated from the source shown above. The row is not attached to it and nothing is completed there. Check the original application of this batch’s profile: two programs can number the same way.',
    },
  },

  annulation: {
    titre: 'Cancellation',
    texte:
      'Undoes what this batch created at the gallery, except what has changed since. Reserved to Venio’s management.',
    bouton: 'Cancel this batch',
    rejouer: 'Replay the cancellation',
    aideAnnuler: 'A recent second factor is required.',
    dialogue: {
      titre: 'Cancel this batch?',
      intro:
        'Venio will undo this batch’s migration at the gallery, in packets of about fifty records.',
      supprimeTitre: 'Will be deleted',
      supprime1: 'The records this batch created, if they are as it left them.',
      supprime2:
        'What came with them: images, an artist created along the way, and their files (originals, variants, attachments).',
      conserveTitre: 'Will be kept',
      conserve1: 'Any record modified since the migration.',
      conserve2:
        'Any record something points to: a reservation, an invoice, an exhibition, another batch… The report says which.',
      conserve3: 'The fields this batch only completed: a completion is never undone.',
      apresTitre: 'Afterwards',
      apres1: 'The batch can no longer be relaunched. To migrate its rows, upload a new batch.',
      apres2: 'No batch of the gallery starts until the cancellation is done.',
      mfa: 'It requires a recent second factor: you will be asked to verify.',
      prevenue: 'I have warned the gallery.',
      prevenueAide:
        'A gallery invited early may already be writing: a write during the cancellation could leave a dangling reference.',
      confirmer: 'Cancel this batch',
      garder: 'Do not cancel',
    },
    suivi: {
      titre: 'Cancellation',
      demandee: 'Cancellation requested by {{par}} on {{date}}. It is about to start.',
      en_cours: 'Cancellation running, requested by {{par}} on {{date}}.',
      echouee: 'The cancellation failed. Replay it: it restarts and only undoes what is left.',
      faite: 'Cancellation done on {{date}}, requested by {{par}}.',
    },
    rapport: {
      titre: 'Cancellation report',
      compteurs: 'Cancellation counters',
      supprimes: 'Records deleted',
      conserves: 'Records kept',
      dejaAbsents: 'Already gone',
      nonDefaites: 'Completions kept',
      nonDefaitesAide: 'Empty fields filled by this batch: a completion is never undone.',
      medias: 'Media removed',
      mediasAide: 'Originals, variants and attachments removed from storage.',
      sansCible_one: '{{count}} creation with no known record (earlier receipt): it stays.',
      sansCible_other: '{{count}} creations with no known record (earlier receipts): they stay.',
      supprimesParType: 'Deleted, by type',
      gardees: 'Records kept, and why',
      aucuneGardee: 'Nothing is kept: everything the batch had created is gone.',
      dejaAbsentes: 'Already gone',
      fiche: '{{type}} {{id}}',
      ligne: 'line {{numero}}',
      et_one: 'and {{count}} more',
      et_other: 'and {{count}} more',
      indisponible: 'This report is unavailable.',
      chargement: 'Reading the report…',
      modeles: {
        Location: 'Location',
        Artist: 'Artist',
        Artwork: 'Artwork',
        Contact: 'Contact',
        Exhibition: 'Exhibition',
        FactureHistorique: 'Earlier invoice',
        Document: 'Document',
        Media: 'Image',
      },
      motifs: {
        modifie: 'Modified since the migration',
        etat_inconnu: 'Migrated before records were tracked: unknown state, kept to be safe',
        referencee: 'Pointed to by {{type}}',
        autre_lot: 'Completed by another batch',
        variantes: 'Its variants are still being made',
      },
      references: {
        oeuvre: 'an artwork',
        exemplaire: 'a copy',
        exposition: 'an exhibition',
        accrochage: 'a hanging',
        mouvement: 'a movement',
        actualite: 'a news item',
        presse: 'a press review',
        contrat: 'a contract',
        contrat_artiste: 'an artist contract',
        document: 'a document',
        image: 'an image',
        image_principale: 'an artwork’s main image',
        portrait: 'an artist portrait',
        logotype: 'a logotype',
        constat: 'a condition-report photo',
        demande: 'an enquiry',
        reservation: 'a reservation',
        salle_privee: 'a private room',
        facture: 'an invoice',
        facture_anterieure: 'an earlier invoice',
        certificat: 'a certificate',
        offre: 'an offer',
        rendez_vous: 'an appointment',
        provenance: 'a provenance',
        cout_production: 'a production cost',
        dossier: 'an artwork folder',
        newsletter: 'a newsletter campaign',
        diffusion: 'a mailing',
        commande: 'a shop order',
        ordre_artiste: 'an artist’s artwork order',
        lieu: 'a location',
        vente: 'a sale',
        fusion: 'a contact merge',
        consultation: 'a document view',
        operation_stand: 'a fair-stand operation',
      },
      motifInconnu: 'Kept ({{motif}})',
    },
    morceaux: {
      titre: 'This batch is split into chunks',
      texte:
        'It is not cancelled in one go: cancel its chunks one by one, in the reverse order of the plan, from last to first.',
      ordre: 'Order to follow',
      ouvrir: 'Open chunk {{rang}} of {{nature}}',
      nom: '{{nature}}, chunk {{rang}}',
    },
    lotAnnule: {
      titre: 'Batch cancelled',
      texte:
        'This batch is cancelled: it can no longer be relaunched. To migrate its rows, upload a new batch.',
    },
    scelle: {
      titre: 'Batch sealed',
      texte: 'Sealed on {{date}}: this batch can no longer be cancelled.',
    },
    badges: {
      scelle: 'Sealed',
      annule: 'Cancelled',
      annulationEnCours: 'Cancelling',
    },
    planActif:
      'A plan occupies the gallery, even paused or blocked: abandon it before cancelling a batch.',
    importEnCours: 'An import is running in the gallery: wait for it to finish before cancelling.',
    galerieEnService:
      'The gallery is live: its batches can no longer be cancelled. A write by the gallery could point to what the cancellation would delete.',
  },

  erreurs: {
    rien_a_lancer: 'No batch is ready to launch in this gallery. Try the uploaded batches first.',
    plan_en_cours: 'A migration plan already occupies this gallery. Resume it or abandon it.',
    plan_absent: 'This action matches no plan: it may have changed. Reload the page.',
    plan_modifie: 'The plan has just changed. Reload the page.',
    plan_actif:
      'A plan occupies the gallery, even paused or blocked. Abandon it before cancelling a batch or sealing the migration.',
    annulation_en_cours:
      'A cancellation is not done in this gallery. No batch launches and the migration cannot be sealed until it is finished. Replay it if it failed.',
    reprise_en_cours: 'A migration import is running in this gallery. Wait for it to finish.',
    rien_a_ecarter: 'The trial refused no row: there is nothing to set aside.',
    lot_non_essaye: 'This batch must have been tried before rows can be set aside.',
    essai_a_refaire:
      'This trial does not say which rows it refused. Run the trial again before setting rows aside.',
    ecartement_perime:
      'The set-aside rows no longer describe this batch’s files. Run the trial again, then set rows aside again.',
    lot_scelle: 'This batch is sealed: it can no longer be cancelled.',
    reprise_close: 'The switchover has started: this gallery’s migration is closed.',
    lot_mis_de_cote: 'This batch was set aside when acceptance began: it no longer runs.',
    lot_annule:
      'This batch was cancelled: it can no longer be relaunched. Upload a new batch to migrate its rows.',
    lot_non_execute: 'Only a finished or failed batch can be cancelled.',
    galerie_en_service:
      'The gallery is live, or has no opening file: its batches can no longer be cancelled.',
    lot_meme_nature_en_cours:
      'A batch of this kind is already running in the gallery. Wait for it to finish.',
  },
};
